import React, { useState } from 'react';
import { requestOtp, verifyOtp } from '@/api/client';
import AppFooter from '@/components/layout/AppFooter';

interface LoginProps {
  onAuthenticated: () => void;
}

type Step = 'email' | 'otp';

// Native HTML elements rather than the shadcn Card/Input/Label/Alert
// wrappers. The .jsx wrappers use React.forwardRef without typed props,
// so a .tsx consumer trips TS2322 (children doesn't exist on
// IntrinsicAttributes). The login screen stays visually consistent
// because it just reuses the same Tailwind tokens (bg-card, border,
// text-muted-foreground, ring-primary, etc.).
export default function Login({ onAuthenticated }: LoginProps) {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  async function handleRequestOtp(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    if (!email.trim()) {
      setError('Enter your email to continue.');
      return;
    }
    setPending(true);
    try {
      await requestOtp(email.trim());
      setStep('otp');
      setInfo('A 6-digit code has been sent. It expires in 5 minutes.');
    } catch {
      setError('Network error — please try again.');
    } finally {
      setPending(false);
    }
  }

  async function handleVerifyOtp(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!/^\d{6}$/.test(code)) {
      setError('Enter the 6-digit code from your email.');
      return;
    }
    setPending(true);
    try {
      await verifyOtp(email.trim(), code);
      onAuthenticated();
    } catch {
      setError('Invalid or expired code.');
    } finally {
      setPending(false);
    }
  }

  const inputClass =
    'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 ' +
    'text-sm ring-offset-background placeholder:text-muted-foreground ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ' +
    'focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';
  const primaryBtn =
    'inline-flex h-10 w-full items-center justify-center rounded-md bg-primary ' +
    'px-4 text-sm font-medium text-primary-foreground transition-colors ' +
    'hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 ' +
    'focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 ' +
    'disabled:pointer-events-none';
  const ghostBtn =
    'inline-flex h-10 w-full items-center justify-center rounded-md text-sm ' +
    'font-medium text-muted-foreground hover:text-foreground disabled:opacity-50';
  const outlineBtn =
    'inline-flex h-10 w-full items-center justify-center rounded-md border ' +
    'border-input bg-background px-4 text-sm font-medium text-foreground ' +
    'disabled:opacity-60 disabled:cursor-not-allowed';
  const labelClass = 'text-sm font-medium leading-none';

  return (
    <div
      data-testid="login-screen"
      className="min-h-screen flex items-center justify-center bg-background px-4"
    >
      <div className="w-full max-w-md rounded-xl border bg-card text-card-foreground shadow">
        <div className="flex flex-col items-center space-y-2 p-6 pb-2 text-center">
          <div
            className="text-2xl font-semibold leading-none tracking-tight"
            style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
          >
            Draft Studio
          </div>
          <div className="text-sm text-muted-foreground">
            Sign in with your email — we'll send you a one-time code.
          </div>
        </div>

        <div className="space-y-4 p-6 pt-4">
          {error && (
            <div
              role="alert"
              data-testid="login-error"
              className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {error}
            </div>
          )}
          {info && !error && (
            <div
              role="status"
              data-testid="login-info"
              className="rounded-md border bg-muted px-3 py-2 text-sm text-muted-foreground"
            >
              {info}
            </div>
          )}

          {step === 'email' && (
            <form onSubmit={handleRequestOtp} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="login-email" className={labelClass}>Email</label>
                <input
                  id="login-email"
                  data-testid="login-email-input"
                  className={inputClass}
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@yourfirm.com"
                  disabled={pending}
                  autoFocus
                />
              </div>
              <button
                type="submit"
                data-testid="login-request-otp-btn"
                className={primaryBtn}
                disabled={pending}
              >
                {pending ? 'Sending…' : 'Send OTP'}
              </button>
            </form>
          )}

          {step === 'otp' && (
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="login-otp" className={labelClass}>6-digit code</label>
                <input
                  id="login-otp"
                  data-testid="login-otp-input"
                  className={inputClass}
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  disabled={pending}
                  autoFocus
                />
                <p className="text-xs text-muted-foreground">Sent to {email}</p>
              </div>
              <button
                type="submit"
                data-testid="login-verify-btn"
                className={primaryBtn}
                disabled={pending}
              >
                {pending ? 'Verifying…' : 'Verify'}
              </button>
              <button
                type="button"
                className={ghostBtn}
                disabled={pending}
                onClick={() => {
                  setStep('email');
                  setCode('');
                  setError(null);
                  setInfo(null);
                }}
              >
                Use a different email
              </button>
            </form>
          )}

          <div className="pt-2">
            <button
              type="button"
              className={outlineBtn}
              disabled
              title="Coming soon"
              data-testid="login-google-btn"
            >
              Continue with Google (coming soon)
            </button>
          </div>
        </div>
        <AppFooter className="mt-6" includeCompliance={false} />
      </div>
    </div>
  );
}
